import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CargomaticModal from 'components/v2/modal';
import { getDocumentTypes } from 'utils/DocumentsAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import SessionStore from 'stores/SessionStore';
import Alert from '../../ui/Alert';
import CargomaticSelect from '../../v2/select';
import { DocumentField } from './DocumentField';
import { DocumentsPreview } from './DocumentsPreview';
import {
  BILL_OF_LADING,
  CUSTOM,
  shipmentDocTypes,
  orderDocTypes,
  carrierPortalDocTypes,
  shipperPortalDocTypes,
} from '../../../config/documents';
import { JOB_CLOSED } from '../../../constants/JobStatus';

const POD_REGEX = /(pod*)|(deliver*)|(proof*)/;

const TypesLoading = () => {
  const { t } = useTranslation();

  return (
    <div className="flex justify-start">
      <LoadingCircle />
      {t('GETTING_DOCUMENT_TYPES')}...
    </div>
  );
};

export function DocumentsPreviewModal({
  show,
  onHide,
  files,
  shipment,
  carrierDocumentUpload,
  onUpload,
  parentType,
}) {
  const { t } = useTranslation();
  const [showBOLMessage, setShowBOLMessage] = useState(false);
  const [error, setError] = useState('');
  const [type, setType] = useState(null);
  const [customName, setCustomName] = useState('');
  const [signatory, setSignatory] = useState(null);
  const [showSignatoryCapture, setShowSignatoryCapture] = useState(false);
  const [types, setTypes] = useState([]);

  const userType = SessionStore.getUserType();

  // Leaving this unused for when we return to integrate the doc service API
  const fetchDocumentTypes = async () => {
    try {
      const { data, errors } = await getDocumentTypes(parentType);

      if (errors && errors.length) {
        setError(errors.join('\n'));
      }

      if (data) {
        const { getAvailableDocumentTypes } = data;
        const filteredTypes = getAvailableDocumentTypes.filter((type) =>
          Boolean(type.canUpload),
        );

        setTypes(filteredTypes);
      }
    } catch (error) {
      console.error('Error fetching document types:', error);
    }
  };

  const bolNotAllowed = (type, shipment) => {
    return (
      type === BILL_OF_LADING.value &&
      shipment.status !== JOB_CLOSED &&
      shipment.shipment_type === 'drayage'
    );
  };

  const handleDocumentTypeSelect = (item) => {
    const { value: type } = item;

    if (bolNotAllowed(type, shipment)) {
      setShowBOLMessage(true);
    }

    if (type !== CUSTOM.value) {
      setCustomName('');
    }

    setType(type);
  };

  const handleUpload = async () => {
    if (!type) {
      setError(t('SELECT_DOCUMENT_NAME'));
      return;
    }

    if (
      carrierDocumentUpload === true &&
      type === CUSTOM.value &&
      shipment.status !== JOB_CLOSED &&
      POD_REGEX.test(customName.toLowerCase())
    ) {
      setError(t('DOCUMENT_REQUIRES_JOB_COMPLETE'));
      return;
    }

    if (bolNotAllowed(type, shipment)) {
      setShowBOLMessage(true);
      return;
    }

    if (type === CUSTOM.value && !customName) {
      setError(t('PROVIDE_CUSTOM_DOCUMENT_NAME'));
      return;
    }

    onHide();
    setSignatory(null);
    setShowSignatoryCapture(false);

    const filename =
      type === CUSTOM.value
        ? customName
        : types.find((currentType) => currentType.value === type)?.name ?? type;

    const renamedFiles = files.map(
      (file) =>
        new File([file], filename.toString(), {
          ...file.options,
          type: file.type,
        }),
    );

    return onUpload(renamedFiles, type, signatory);
  };

  useEffect(() => {
    if (show) {
      if (parentType === 'order') {
        setTypes(orderDocTypes);
      } else if (userType === 'Admin') {
        setTypes(shipmentDocTypes);
      } else if (userType === 'Carrier') {
        const filteredCarrierPortalDocTypes = carrierPortalDocTypes.filter(doc => doc.value !== "custom");
        setTypes(filteredCarrierPortalDocTypes);
      } else if (userType === 'Shipper') {
        setTypes(shipperPortalDocTypes);
      }
    }
  }, [show]);

  const renderBody = () => {
    return (
      <>
        {showBOLMessage && (
          <Alert type="error" onDismiss={() => setShowBOLMessage(false)}>
            {t('BOL_AVAILABLE_FOR_CLOSE_SHIPMENTS')}
          </Alert>
        )}
        {error && (
          <div>
            <Alert type="warning">{error}</Alert>
            <hr />
          </div>
        )}
        <div className="overflow-hidden">
          <DocumentsPreview files={files} />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          {types?.length ? (
            <CargomaticSelect
              label={t('DOCUMENT_TYPES')}
              selected={type}
              items={types}
              onSelect={handleDocumentTypeSelect}
              caption={t('PLEASE_SELECT_A_DOCUMENT_TYPE')}
              valueKey="value"
              textKey="localizationSlug"
              showCaption
            />
          ) : (
            <TypesLoading />
          )}
          {type === 'custom' && (
            <DocumentField
              id="customTitle"
              label={t('CUSTOM_TITLE')}
              placeholder={t('ENTER_DOCUMENT_TITLE')}
              value={customName}
              onChange={setCustomName}
            />
          )}
          {showSignatoryCapture && (
            <DocumentField
              id="signatory"
              label={t('POD_NAME')}
              placeholder={t('SIGNATORY')}
              value={signatory}
              onChange={setSignatory}
            />
          )}
        </div>
      </>
    );
  };

  const renderFooter = () => {
    return (
      <div className="flex justify-end items-center mt-4">
        <button
          className="button button-green carrier-admin-attachDocument heap"
          onClick={handleUpload}
        >
          {t('UPLOAD')}
        </button>
      </div>
    );
  };

  return (
    <CargomaticModal
      show={show}
      title={t('PREVIEW')}
      onHide={onHide}
      body={renderBody()}
      footer={renderFooter()}
    />
  );
}
